import { now, user } from 'src/StateObserver';
import { isPlatformNative } from 'src/lib/native/native.util';
import {
  mapFeatureToDialogOrigin,
  openPopupPromise,
} from 'src/lib/popups/popupOpenClose';
import { captureGenericError } from 'src/lib/sentry';
import { State } from 'src/replicant/State';
import { shopBundlesHasPurchasedPetStarterPack } from 'src/replicant/match2/shopbundles/match2-shopbundles.getters';
import { puzzleCoinsIsPlayingTutorial } from 'src/replicant/puzzlecoins/puzzlecoins.getters';
import { getOpenWebBehavior } from 'src/replicant/ruleset/openweb';
import {
  getRemainingStarterPackPromoTime,
  getStarterPackProductId,
  isPetStarterPackEnabled,
} from 'src/replicant/starterpack/starterpack.getters';

/**
 * The origin features that can trigger the starter pack promo
 */
const starterPackPromoOrigin = ['home-button', 'tutorial'] as const;

/**
 * The type of the starter pack promo origin
 */
type StarterPackPromoOrigin = (typeof starterPackPromoOrigin)[number];

/**
 * Check if the starter pack promo is enabled for the user
 * @param state
 * @returns
 */
export function isPetStarterPackAvailable(state: State) {
  // Never enabled on native platforms
  if (isPlatformNative()) {
    return false;
  }

  // Not enabled if user is not in default web behavior
  if (getOpenWebBehavior(state) !== 'default_web') {
    return false;
  }

  // Not enabled if user is in liff
  if (window.liff?.isInClient()) {
    return false;
  }

  // Check if the AB test is enabled
  return isPetStarterPackEnabled(state);
}

/**
 * Check if starter pack promo can be presented to the user
 * @param state
 * @param now
 * @returns
 */
export function canShowStarterPackPromo(state: State, now: number) {
  // Check if the user is in one of the AB test buckets
  if (!isPetStarterPackAvailable(state)) {
    return false;
  }

  // Can't show starter pack promo if user has already purchased pet starter pack
  if (shopBundlesHasPurchasedPetStarterPack(state)) {
    return false;
  }

  // Can't show promo during tutorial
  if (puzzleCoinsIsPlayingTutorial(state)) {
    return false;
  }

  // Can't show starter pack promo if the promo time has expired
  if (getRemainingStarterPackPromoTime(state, now) <= 0) {
    return false;
  }

  return true;
}

/** Allow the starter pack home button to be shown */
let homeButtonAllowed = true;

/**
 * Allow or disallow the starter pack home button to be shown - set to false wil prevent the button to be shown,
 * but set to tru will also depends on other conditions like AB test bucket
 * @param value
 */
export function allowStartertPackHomeButton(value: boolean) {
  homeButtonAllowed = value;
}

/**
 * Check if the starter pack home button can be shown
 * @param state
 * @param now
 * @returns
 */
export function canShowStarterPackHomeButton(state: State, now: number) {
  if (!homeButtonAllowed) {
    return false;
  }

  // Can't show home button if cannot show starter pack promo
  if (!canShowStarterPackPromo(state, now)) {
    return false;
  }

  // This feature disabled by default for now
  return false;
}

/**
 * Present the starter pack promo to the user
 * @param args
 */
async function showStarterPackPromo(args: {
  originFeature: StarterPackPromoOrigin;
}) {
  // Check if the starter pack promo can be shown
  if (!canShowStarterPackPromo(user(), now())) {
    return;
  }

  const productId = getStarterPackProductId(user());

  // Check if product id is available, otherwise log an error
  if (!productId) {
    captureGenericError('PetStarterPackError', 'No product id found');
    return;
  }

  await openPopupPromise('popupPetStarterPack', {
    productId,
    origin: mapFeatureToDialogOrigin(args.originFeature),
  });

  // Always allow showing the home button after the promo popup
  allowStartertPackHomeButton(true);
}

/**
 * Show starter pack promo after the shop
 * @returns
 */
export async function showStarterPackPromoFromHomeButton() {
  if (!canShowStarterPackPromo(user(), now())) {
    return;
  }
  await showStarterPackPromo({ originFeature: 'home-button' });
}

/**
 * Show starter pack promo after the shop
 * @returns
 */
export async function showStarterPackPromoFromTutorial() {
  if (!canShowStarterPackPromo(user(), now())) {
    return;
  }
  await showStarterPackPromo({ originFeature: 'tutorial' });
}
